<template>
    <VJoyPanel :id="anchorsPageProfile.categoriesSection" data-testid="categories-section" padding="large">
        <template #panel-title>
            <h2>{{ t('profile.section.categories.title') }}</h2>
        </template>

        <template v-if="profile?.editMode" #panel-title-action>
            <ClientOnly>
                <VJoyIconButton variant="ghost" icon="pen" size="small" :label="$t('general.edit')" @click="goToEditCategories" />
                <template #fallback>
                    <SectionActionSkeleton />
                </template>
            </ClientOnly>
        </template>

        <template v-if="profile" #panel-body>
            <div class="categories-content">
                <div v-for="family in orderedFamilies(profile.categorization)" :key="family.id" class="categories">
                    <FamilyIcon size="small" :family="family.pictoName as JoyIconsId" />
                    <ul v-if="family.categories.length > 0" class="categories__list">
                        <li v-for="category in orderedCategories(family.categories)" :key="category.id" class="categories__list-item">
                            <UncrawlableLink
                                v-if="sectionVisibility[SectionsVisibilityType.SEARCH_LINKS] && category.seoUrl && isSearchSeoUrl(category.seoUrl)"
                                v-slot="{navigate}"
                                custom
                                external
                                :to="$withLocalePrefix(category.seoUrl)"
                            >
                                <VJoyLink class="categories__list-item-link" @click="navigate">
                                    {{ category.label }}
                                </VJoyLink>
                            </UncrawlableLink>
                            <VJoyLink
                                v-else-if="sectionVisibility[SectionsVisibilityType.SEARCH_LINKS] && category.seoUrl"
                                class="categories__list-item-link"
                                :href="$withLocalePrefix(category.seoUrl)"
                            >
                                {{ category.label }}
                            </VJoyLink>
                            <span v-else class="categories__list-item-label">{{ category.label }}</span>
                            <VJoyTag
                                v-if="category.id === profile?.categorization?.preferredCategory && isEditionEnabled"
                                :label="t('profile.creation.predictor.wizard.v2.details.expertise.main')"
                                variant="secondary"
                                size="xsmall"
                            />
                        </li>
                    </ul>
                    <ul v-else class="categories__list">
                        <li :key="family.id" class="categories__list-item">
                            <span>{{ family.label }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {UncrawlableLink} from '#components';
    import {useTranslation} from '#imports';
    import {VJoyIconButton, VJoyLink, VJoyPanel, VJoyTag} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import SectionActionSkeleton from '~/components/section/SectionActionSkeleton.vue';
    import {useProfilePageViewMode} from '~/composables/useProfilePageViewMode';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {orderedCategories, orderedFamilies} from '~/types/domain/Categorization';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';
    import FamilyIcon from './FamilyIcon.vue';

    const {t} = useTranslation();
    const {profile} = storeToRefs(useProfileStore());
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const {isEditionEnabled} = useProfilePageViewMode();

    const isSearchSeoUrl = (seoUrl: string): boolean => seoUrl.startsWith('/s?q=');

    function goToEditCategories() {
        eventBus.emit('categories:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .categories-section {
        scroll-margin-top: calc(var(--header-menu-height) + var(--joy-core-spacing-2));
    }

    .categories-content {
        display: flex;
        gap: var(--joy-core-spacing-9);
        align-items: flex-start;

        @include mq.screen_xs {
            flex-direction: column;
            gap: var(--joy-core-spacing-4);
        }
    }

    .categories {
        display: flex;

        &__list {
            display: flex;
            flex-direction: column;
            justify-content: center;
            list-style-type: none;
            padding: 0;
            margin: 0 0 0 var(--joy-core-spacing-2);
            font-size: var(--joy-font-size-primary-400);

            &-item {
                display: flex;
                column-gap: var(--joy-core-spacing-1);
                align-items: center;

                &-label,
                &-link {
                    font-weight: var(--joy-font-weight-bold);
                }
            }
        }
    }
</style>
